import { formatCompactMoney, formatStage } from "@/lib/format";
import type { Case, Cents, DecisionOutcome, DecisionRecommendation } from "@/lib/types";
import { readCase, writeCase } from "./store";
import { appendActivity } from "./activity";

export async function getDecision(caseId: string): Promise<DecisionRecommendation | null> {
  return (await readCase(caseId))?.decision ?? null;
}

/** Result of a write: the decision and the case, whose stage may have moved. */
export interface DecisionUpdate {
  decision: DecisionRecommendation;
  case: Case;
}

async function update(
  caseId: string,
  mutate: (record: { decision: DecisionRecommendation; case: Case }) => void,
): Promise<DecisionUpdate | null> {
  const record = await writeCase(caseId, mutate);
  return record ? { decision: record.decision, case: record.case } : null;
}

export async function setRecommendedAmount(
  caseId: string,
  amount: Cents,
  actor: string,
): Promise<DecisionUpdate | null> {
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("A recommended amount must be a positive figure.");
  }

  let from: Cents | null = null;
  const result = await update(caseId, (record) => {
    if (record.decision.amount === amount) return;
    from = record.decision.amount;
    record.decision.amount = amount;
  });

  if (from !== null) {
    await appendActivity(caseId, {
      actor,
      kind: "decision",
      title: "Changed recommended amount",
      from: formatCompactMoney(from),
      to: formatCompactMoney(amount),
    });
  }

  return result;
}

export async function toggleCondition(
  caseId: string,
  conditionId: string,
  selected: boolean,
  actor: string,
): Promise<DecisionUpdate | null> {
  // Captured on a property rather than in a plain `let`: TypeScript does not
  // track an assignment made inside the callback, and narrows a `let` that was
  // initialised to null down to `never` by the time it is read back.
  const captured: { label: string | null } = { label: null };

  const result = await update(caseId, (record) => {
    const condition = record.decision.conditions.find((entry) => entry.id === conditionId);
    if (!condition || condition.selected === selected) return;
    condition.selected = selected;
    captured.label = condition.label;
  });

  if (captured.label !== null) {
    const label = captured.label;
    await appendActivity(caseId, {
      actor,
      kind: "decision",
      title: selected ? `Condition added — ${label}` : `Condition removed — ${label}`,
    });
  }

  return result;
}

const OUTCOME_STAGE = {
  approved: "approved",
  rejected: "rejected",
  info_requested: "data_collection",
} as const;

const OUTCOME_TITLE: Record<DecisionOutcome, string> = {
  approved: "Approved",
  rejected: "Rejected",
  info_requested: "More information requested",
};

/**
 * Commits the decision. This moves the case stage, which is why it returns the
 * case as well — the header above every tab has to change with it.
 */
export async function recordOutcome(
  caseId: string,
  outcome: DecisionOutcome,
  actor: string,
  note?: string,
): Promise<DecisionUpdate | null> {
  let fromStage: string | null = null;
  let amount: Cents | null = null;

  const result = await update(caseId, (record) => {
    fromStage = formatStage(record.case.stage);
    amount = record.decision.amount;
    record.decision.outcome = outcome;
    record.case.stage = OUTCOME_STAGE[outcome];
  });

  if (result) {
    const conditions = result.decision.conditions.filter((c) => c.selected).length;
    await appendActivity(caseId, {
      actor,
      kind: "decision",
      title: OUTCOME_TITLE[outcome],
      detail:
        outcome === "approved" && amount !== null
          ? `${formatCompactMoney(amount)} · ${conditions} condition${conditions === 1 ? "" : "s"}`
          : undefined,
      from: fromStage ?? undefined,
      to: formatStage(result.case.stage),
      reason: note?.trim() || undefined,
    });
  }

  return result;
}
